import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { BehaviorSubject, lastValueFrom } from 'rxjs';
import {
  LoginStateObject,
  LoginInitialState,
  LoginState,
} from '../types/login.types';

import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { MsalService } from '@azure/msal-angular';
import {
  FacebookUser,
  GoogleUser,
  GoogleProfileResponse,
  MicrosoftUser,
  FacebookLoginResponse,
  FacebookProfile,
} from '../types/oauth.type';

declare var FB: any;
declare var AppleID: any;
@Injectable({
  providedIn: 'root',
})
export class LoginService {
  // SDK LOADING VARIABLES
  private msalInitialized = false;

  // CONSTRUCTOR
  constructor(
    private msalService: MsalService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.msalService.initialize().subscribe(() => {
        this.msalInitialized = true;
      });
    }
  }

  //////////////////////////////
  // STATE MANAGEMENT
  //////////////////////////////
  // Initial state
  private initialState: LoginInitialState = {
    type: LoginState.login,
    timestamp: new Date(),
  };

  private loginStateSubject = new BehaviorSubject<LoginStateObject>(
    this.initialState
  );
  // Observable para que otros servicios puedan suscribirse
  public loginState$ = this.loginStateSubject.asObservable();

  // Get current state
  get currentState(): LoginStateObject {
    return this.loginStateSubject.value;
  }

  // Notify with new state object
  notifyLoginState(state: LoginStateObject) {
    this.loginStateSubject.next(state);
  }
  //////////////////////////////

  async getGoogleUserInfo(accessToken: string): Promise<GoogleProfileResponse> {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const profile = await response.json();
    return profile;
  }
  //////////////////////////////

  //////////////////////////////
  // LOGIN WITH SOCIAL NETWORKS
  //////////////////////////////
  async loginWithGoogle(): Promise<GoogleUser> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('Google OAuth no está disponible en el servidor');
    }

    return new Promise((resolve, reject) => {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: environment.googleOAuth.clientId,
        scope: 'openid email profile',
        callback: async (response: any) => {
          console.log('response', response);
          if (response.error) return reject(response);

          this.getGoogleUserInfo(response.access_token).then((userInfo) => {
            resolve({
              idToken: userInfo.sub,
              accessToken: response.access_token,
              email: userInfo.email,
              displayName: userInfo.name,
            });
          });
        },
      });

      client.requestAccessToken();
    });
  }

  async loginWithFacebook(): Promise<FacebookUser> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('Facebook OAuth no está disponible en el servidor');
    }

    return new Promise((resolve, reject) => {
      FB.login(
        (response: FacebookLoginResponse) => {
          if (response.status === 'connected' && response.authResponse) {
            const accessToken = response.authResponse.accessToken;
            FB.api(
              '/me',
              { fields: 'id,name,email' },
              (profile: FacebookProfile) => {
                resolve({
                  idToken: profile.id,
                  email: profile.email,
                  displayName: profile.name,
                  accessToken: accessToken,
                });
              }
            );
          } else {
            reject('Usuario canceló el login o no autorizó.');
          }
        },
        { scope: environment.facebookOAuth.scope }
      );
    });
  }

  async loginWithMicrosoft(): Promise<MicrosoftUser> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('Microsoft OAuth no está disponible en el servidor');
    }

    if (!this.msalInitialized) {
      throw new Error('Microsoft OAuth no inicializado');
    }

    const loginRequest = {
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account',
    };

    const response: AuthenticationResult = await lastValueFrom(
      this.msalService.loginPopup(loginRequest)
    );

    const account: AccountInfo | null = response.account;

    return {
      idToken: response.idToken,
      email: account?.username,
      displayName: account?.name,
    };
  }

  async loginWithApple(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('Apple OAuth no está disponible en el servidor');
    }

    try {
      await AppleID.auth.signIn();
    } catch (error) {
      console.error('Apple Sign In Error:', error);
      throw error;
    }
  }
}
